import React from 'react';
import { useLessonReviewReadOnly } from './lessonReviewReadOnly';
import { Alert, Box, Stack, TextField } from '@mui/material';
import { lessonImageUrl } from '../lessonImageUrl';
import VerbaScholaImageCandidateControls from './VerbaScholaImageCandidateControls';
import { ExerciseImageRequestActivity } from './exerciseImageGenerationAvailability';

interface Props {
    lessonId: number;
    label: string;
    value: string;
    altText?: string;
    concept?: string;
    meaning?: string;
    lessonContext?: string;
    studentTask?: string;
    distractors?: string[];
    onChange: (url: string, altText: string) => void;
}

function isSafePictureUrl(value: string): boolean {
    if (!value) return true;
    if (value.includes('\\') || [...value].some(character => character.charCodeAt(0) < 32)) return false;
    if (value.startsWith('/')) return !value.startsWith('//');
    try {
        const parsed = new URL(value);
        return ['http:', 'https:'].includes(parsed.protocol) && Boolean(parsed.hostname);
    } catch { return false; }
}

export function hasPictureDescription(url: string | null | undefined, altText: string | null | undefined): boolean {
    return !url?.trim() || Boolean(altText?.trim() && altText.trim().length <= 500);
}

/** A picture can come from an existing URL, a teacher upload, or a new illustration. */
export default function VerbaScholaExerciseImageField({ lessonId, label, value, altText = '', concept = '', meaning = '', lessonContext = '', studentTask = '', distractors = [], onChange }: Props) {
    const onBusyChange = React.useContext(ExerciseImageRequestActivity);
    const readOnly = useLessonReviewReadOnly();
    const [imageFailed, setImageFailed] = React.useState(false);
    React.useEffect(() => { setImageFailed(false); }, [value]);
    const missingDescription = Boolean(value?.trim()) && !hasPictureDescription(value, altText);
    const safeUrl = isSafePictureUrl(value);
    return <Stack spacing={1.25} role="group" aria-label={label} data-exercise-picture-field={label} tabIndex={-1}
        sx={{ scrollMarginTop: 90, '&:focus-visible': { outline: '3px solid #2563eb', outlineOffset: 4, borderRadius: 1 } }}>
        <TextField size="small" label={`${label} URL`} value={value} onChange={event => onChange(event.target.value, altText)} fullWidth
            error={!safeUrl} helperText={!safeUrl ? 'Use an https:// or http:// URL, or a path beginning with /.' : undefined} />
        <TextField size="small" label={`${label} description`} value={altText} onChange={event => onChange(value, event.target.value)} fullWidth
            inputProps={{ maxLength: 500 }} error={missingDescription}
            helperText={missingDescription ? 'Describe what the picture shows before saving.' : 'Describe what the picture shows for learners using a screen reader.'} />
        {value && safeUrl && (imageFailed
            ? <Alert severity="warning">The picture could not be loaded. Check its URL before saving.</Alert>
            : <Box component="img" src={lessonImageUrl(value)} alt={altText || label} onError={() => setImageFailed(true)}
                sx={{ width: '100%', maxWidth: 320, maxHeight: 240, objectFit: 'contain', borderRadius: 1, bgcolor: '#f6f7f9' }} />)}
        {!readOnly && <VerbaScholaImageCandidateControls lessonId={lessonId} label={label} value={value} altText={altText}
            concept={concept} meaning={meaning} lessonContext={lessonContext} studentTask={studentTask} distractors={distractors}
            onChange={onChange} onBusyChange={onBusyChange} />}
    </Stack>;
}
